import { createFileRoute } from "@tanstack/react-router";
import LevelCard from "@/components/LevelCard";
import PageContent from "@/components/PageContent";
import TitleBar from "@/components/layout/TitleBar";

import { aseExamsMap } from "@/data/aseExams";
import { evtccExamsMap } from "@/data/evtccExams";
import { getDownloadUrl } from "@/lib/media";

export const Route = createFileRoute('/certifications')({
  component: Certifications,
})

function Certifications() {
  return (
    <>
      <TitleBar />
      <PageContent>
        <section>
          <h1>
            Certification Tracks
          </h1>
          <p>
            The EVTCC Certification Program has four technician certification
            tracks and a management track. The technician certification tracks
            are for fire apparatus, ambulance, law enforcement, and airport
            rescue and fire fighting (ARFF) vehicle technicians. Technicians
            have two options of becoming EVT Certified.
          </p>
          <ul>
            <li>
              Option 1: Technicians can become certified in the specific test
              area(s) by taking only the EVT exams. Each EVTCC test passed is a standalone credential in its own right. For a more comprehensive professional qualification, you may consider certifying in EVTCC levels, explained in Option 2.
            </li>
            <li>
              Option 2: Technicians can attain the highest EVTCC certification
              status by combining EVT and ASE exams to reach Level I, II and
              Master EVT Certification.
            </li>
          </ul>
          <p>
            Please note that the Law Enforcement Vechicle (LEV) Certification
            Track has only one exam and only one Level. The Management Track
            has a Level I and Level II exam and does not require any ASE
            exams. The Level Certification Tracks are explained below.
          </p>
          <p>
            <a
              href={getDownloadUrl("EVTCC-Level-Application-Form.pdf")}
            >
              Download the level application form
            </a>
          </p>

          <h2>Jump to a section</h2>
          <ul>
            <li>
              <a href="#fire">Fire Apparatus Technician Certification Track</a>
            </li>
            <li>
              <a href="#ambulance">Ambulance Technician Certification Track</a>
            </li>
            <li>
              <a href="#arff">ARFF Technician Certification Track</a>
            </li>
            <li>
              <a href="#lev">LEV Technician Certification Track</a>
            </li>
            <li>
              <a href="#mgmt">Management Certification Track</a>
            </li>
          </ul>
        </section>

        <section id="fire">
          <h2>
            Fire Apparatus Technician Certification Track
          </h2>
          <p>
            The Fire Apparatus Technician Certification Track is for those
            technicians who service and maintain fire department pumpers,
            squads, aerial devices, tankers, wildland apparatus, etc. The Fire
            Apparatus Track exams cover the design and performance standards,
            specialized systems and equipment, and accepted practices used in
            the service and maintenance of fire apparatus. This track has
            three levels of certification. Each level requires specific EVT
            exams and ASE heavy-duty truck exams. See below for the
            requirements for each level.
          </p>

          <div className="grid gap-2 xl:grid-cols-3">
            <LevelCard
              title="Fire Apparatus Level I"
              evtExams={["F1", "F2"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["T4", "T5"].map((examCode) => aseExamsMap.get(examCode)!)} />

            <LevelCard
              title="Fire Apparatus Level II"
              evtExams={["F3", "F4"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["T2", "T3", "T6"].map((examCode) => aseExamsMap.get(examCode)!)} />

            <LevelCard
              title="Fire Apparatus Level III (Master)"
              evtExams={["F5", "F6"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["T1", "T7"].map((examCode) => aseExamsMap.get(examCode)!)} />
          </div>
        </section>

        <section id="ambulance">
          <h2>
            Ambulance Technician Certification Track
          </h2>
          <p>
            The Ambulance Technician Certification Track recognizes
            technicians who have gained specialization in ambulances. The
            requirements for track include EVTCC exams about electrical,
            mechanical, and HVAC aspects of ambulance maintenance, plus
            relevant ASE automotive and truck exams. This track has three
            levels of certification. See below for the requirements for each
            level.
          </p>

          <div className="grid gap-2 xl:grid-cols-3">
            <LevelCard
              title="Ambulance Level I"
              evtExams={["E0", "E1"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["A4", "A5"].map((examCode) => aseExamsMap.get(examCode)!)} />

            <LevelCard
              title="Ambulance Level II"
              evtExams={["E2", "E3"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["A9", "T3", "T4"].map((examCode) => aseExamsMap.get(examCode)!)} />

            <LevelCard
              title="Ambulance Level III (Master)"
              evtExams={["E4"].map((examCode) => evtccExamsMap.get(examCode)!)}
              aseExams={["T1", "T2", "T5"].map((examCode) => aseExamsMap.get(examCode)!)} />
          </div>


          <div className="my-2 mt-4 grid gap-4 md:grid-cols-3">
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level I
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>
                  E0 &ndash; Maintenance, Inspection, and Testing of
                  Ambulances
                </li>
                <li>E1 &ndash; Design &amp; Performance of Ambulances</li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>A4 &ndash; Auto &ndash; Suspension &amp; Steering</li>
                <li>A5 &ndash; Auto &ndash; Brakes</li>
              </ul>
            </div>
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level II
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>E2 &ndash; Ambulance Electrical Systems</li>
                <li>
                  E3 &ndash; Ambulance Heating, Air-Conditioning &amp;
                  Ventilation
                </li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>A9 &ndash; Auto &ndash; Light Vehicle Diesel Engines</li>
                <li>T3 &ndash; Truck &ndash; Drive Train</li>
                <li>T4 &ndash; Truck &ndash; Brakes</li>
              </ul>
            </div>
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level III (Master)
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>E4 &ndash; Ambulance Cab, Chassis and Powertrain</li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>T1 &ndash; Truck &ndash; Gasoline Engines</li>
                <li>T2 &ndash; Truck &ndash; Diesel Engines</li>
                <li>T5 &ndash; Truck &ndash; Suspension &amp; Steering</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-6" id="arff">
          <h2 className="mt-6">
            ARFF Technician Certification Track
          </h2>
          <p>
            The ARFF Technician Certification track program recognizes
            technicians who work on airport rescue and fire-fighting vehicles.
            The exams cover design and performance standards, preventive
            maintenance, chassis and vehicle performance, and the
            extinguishment systems of ARFF vehicles. This track has three
            levels of certification. Each level requires specific EVTCC exams
            and ASE exams. See below for specifics.
          </p>
          <div className="my-2 mt-4 grid gap-4 md:grid-cols-3">
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level I
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>A1 &ndash; Design &amp; Performance of ARFF Vehicles</li>
                <li>
                  F1 &ndash; Maintenance, Inspection, and Testing of Fire
                  Apparatus
                </li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>T2 &ndash; Truck &ndash; Diesel Engines</li>
                <li>T4 &ndash; Truck &ndash; Brakes</li>
                <li>
                  T6 &ndash; Truck &ndash; Electrical / Electronic Systems
                </li>
              </ul>
            </div>
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level II
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>
                  A2 &ndash; Chassis &amp; Vehicle Components of ARFF Vehicles
                </li>
                  <li>F4 &ndash; Fire Apparatus Electrical Systems</li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>T3 &ndash; Truck &ndash; Drive Train</li>
                <li>T5 &ndash; Truck &ndash; Suspension &amp; Steering</li>
                <li>
                  T7 &ndash; Truck &ndash; Heating, Ventilation & Air
                  Conditioning (HVAC)
                </li>
              </ul>
            </div>
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level III (Master)
              </h3>
              <h4>
                EVTCC Exams
              </h4>
              <ul>
                <li>
                  A3 &ndash; Water/Foam and Ancillary Extinguishment Systems
                </li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>
                  L2 &ndash; Electronic Diesel Engine Diagnosis Specialist
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-6" id="lev">
          <h2 className="mt-6">
            LEV Technician Certification Track
          </h2>
          <p>
            The Law Enforcement Technician Certification track program
            recognizes technicians who work on law enforcement vehicles. This
            track has one level of certification and requires specific EVT
            exams and ASE automotive exams. The exam covers specialized
            equipment installation. See below for specifics.
          </p>
          <div className="inline-block">
            <div className="rounded-md border-2 border-black bg-gray-100 pb-4 pl-4 pr-4 pt-2">
              <h3>
                Level I
              </h3>
              <h4>EVTCC Exam</h4>
              <ul>
                <li>L1 &ndash; Law Enforcement Vehicle Installation</li>
              </ul>
              <h4>ASE Exams</h4>
              <ul>
                <li>A1 &ndash; Auto &ndash; Engine Repair</li>
                <li>A4 &ndash; Auto &ndash; Suspension &amp; Steering</li>
                <li>A5 &ndash; Auto &ndash; Brakes</li>
                <li>
                  A6 &ndash; Auto &ndash; Electrical / Electronic Systems
                </li>
                <li>
                  A7 &ndash; Auto &ndash; Heating &amp; Air Conditioning
                </li>
                <li>A8 &ndash; Auto &ndash; Engine Performance</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mgmt">
          <h2>
            Management Track
          </h2>
          <p>
            The Management certification track has a Management Level I exam for shop foremen and Level II for shop supervisors. There are no additional requirements such as other EVTCC or ASE exams.
          </p>
        </section>
      </PageContent>
    </>
  );
}
